import { supabase } from './supabase';
import type { OpeningDay, TenantSocials } from './database.types';
import { sortForSave } from './tenantSettings';

/**
 * Service de réglages resto (étape 2 — sous-étape 3A) — écriture Supabase.
 *
 *  - Horaires/créneaux : via la RPC sécurisée `save_tenant_hours`
 *    (SECURITY DEFINER, vérifie tenant_users / super_admin) — cf. sql FIX_HORAIRES
 *    + sql/29_tenant_vitrine.sql. C'est la voie imposée par la parité (M10).
 *  - Identité (ville, gérant, réseaux, notes) : update direct sur `tenants`,
 *    autorisé par la policy RLS « admin manage tenants » (tenant_users ou super_admin).
 *
 * Logique pure (tri, nettoyage) dans tenantSettings.ts (testée à part).
 */

export interface SaveResult {
  ok: boolean;
  /** Message prêt à afficher si échec. */
  error?: string;
}

export interface SaveHoursInput {
  tenantId: string;
  openingHours: OpeningDay[];
  /** Fermetures exceptionnelles "YYYY-MM-DD". */
  closures: string[];
  /** Pas entre 2 créneaux (min). */
  stepMin: number;
  /** Capacité par créneau. */
  capacity: number;
}

/** Enregistre les horaires via la RPC (créneaux triés avant envoi — M14). */
export async function saveTenantHours(input: SaveHoursInput): Promise<SaveResult> {
  const { error } = await supabase.rpc('save_tenant_hours', {
    p_tenant_id: input.tenantId,
    p_hours: sortForSave(input.openingHours),
    p_closures: input.closures ?? [],
    p_step: input.stepMin,
    p_capacity: input.capacity,
  });
  if (error) {
    return { ok: false, error: `Enregistrement des horaires impossible : ${error.message}` };
  }
  return { ok: true };
}

/**
 * Récupère la NOTE Google d'un resto (note /5 + nb d'avis) via l'Edge `google-rating`
 * (API Places), à partir d'une requête « nom + adresse ». Automatise le remplissage des
 * notes (fini la saisie manuelle qui se périme). Uber n'a pas d'équivalent fiable (pas d'API).
 */
export async function fetchGoogleRating(
  query: string,
): Promise<{ ok: boolean; rating?: number; count?: number; name?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('google-rating', { body: { query } });
  if (error) return { ok: false, error: error.message };
  const res = data as { ok?: boolean; rating?: number; count?: number; name?: string; error?: string } | null;
  if (!res || res.ok === false) return { ok: false, error: res?.error || 'Récupération impossible' };
  return { ok: true, rating: res.rating, count: res.count, name: res.name };
}

export interface SaveInfoInput {
  tenantId: string;
  /** Nom AFFICHÉ du restaurant (colonne `name`). Ne pas passer si vide → on n'écrase pas. */
  name?: string;
  city?: string | null;
  /** Gérant (greeting admin « Bonjour, {manager} ») — colonne manager_name. */
  managerName?: string | null;
  socials?: TenantSocials;
  ratingGoogle?: number | null;
  ratingGoogleCount?: number;
  ratingUber?: number | null;
  ratingUberCount?: number;
  // Vitrine + identité (onboarding self-service : éditables depuis l'admin, plus de SQL par client).
  tagline?: string | null;
  shortPitch?: string | null;
  heroImageUrl?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  addressLine?: string | null;
  postalCode?: string | null;
  phone1?: string | null;
  email?: string | null;
}

/** Met à jour les champs identité/branding du tenant (update direct, RLS admin). */
export async function saveTenantInfo(input: SaveInfoInput): Promise<SaveResult> {
  const patch: Record<string, unknown> = {};
  // name : uniquement s'il est fourni ET non vide (le nom ne doit jamais devenir vide).
  if (input.name !== undefined && input.name.trim() !== '') patch.name = input.name.trim();
  if (input.city !== undefined) patch.city = input.city;
  if (input.managerName !== undefined) patch.manager_name = input.managerName;
  if (input.socials !== undefined) patch.socials = input.socials;
  if (input.ratingGoogle !== undefined) patch.rating_google = input.ratingGoogle;
  if (input.ratingGoogleCount !== undefined) patch.rating_google_count = input.ratingGoogleCount;
  if (input.ratingUber !== undefined) patch.rating_uber = input.ratingUber;
  if (input.ratingUberCount !== undefined) patch.rating_uber_count = input.ratingUberCount;
  if (input.tagline !== undefined) patch.tagline = input.tagline;
  if (input.shortPitch !== undefined) patch.short_pitch = input.shortPitch;
  if (input.heroImageUrl !== undefined) patch.hero_image_url = input.heroImageUrl;
  if (input.logoUrl !== undefined) patch.logo_url = input.logoUrl;
  if (input.primaryColor !== undefined) patch.primary_color = input.primaryColor;
  if (input.secondaryColor !== undefined) patch.secondary_color = input.secondaryColor;
  if (input.addressLine !== undefined) patch.address_line = input.addressLine;
  if (input.postalCode !== undefined) patch.postal_code = input.postalCode;
  if (input.phone1 !== undefined) patch.phone1 = input.phone1;
  if (input.email !== undefined) patch.email = input.email;

  if (Object.keys(patch).length === 0) return { ok: true };

  const { error } = await supabase.from('tenants').update(patch).eq('id', input.tenantId);
  if (error) {
    return { ok: false, error: `Enregistrement des infos impossible : ${error.message}` };
  }
  return { ok: true };
}

/**
 * Champs « extra » (identité légale + lien avis Google) — colonnes ajoutées par
 * SQL_ADMIN_legal_google.sql. Sauvegarde RÉSILIENTE : si les colonnes n'existent
 * pas encore, on n'échoue pas (log seulement), pour ne jamais bloquer la sauvegarde principale.
 */
export interface SaveExtraInput {
  tenantId: string;
  legalName?: string | null;
  siret?: string | null;
  googleReviewUrl?: string | null;
}

/**
 * Bascule Ouvert/Fermé du resto (colonne `is_open`, ajoutée par SQL_ADMIN_legal_google.sql).
 * Résilient : si la colonne n'existe pas encore, on n'échoue pas (la tablette garde l'état local).
 */
export async function setTenantOpen(tenantId: string, isOpen: boolean): Promise<SaveResult> {
  const { error } = await supabase.from('tenants').update({ is_open: isOpen }).eq('id', tenantId);
  if (error) {
    console.warn('[tenantSettings] is_open non enregistré (migration manquante ?):', error.message);
    return { ok: true };
  }
  return { ok: true };
}

export async function saveTenantExtra(input: SaveExtraInput): Promise<SaveResult> {
  const patch: Record<string, unknown> = {};
  if (input.legalName !== undefined) patch.legal_name = input.legalName;
  if (input.siret !== undefined) patch.siret = input.siret;
  if (input.googleReviewUrl !== undefined) patch.google_review_url = input.googleReviewUrl;
  if (Object.keys(patch).length === 0) return { ok: true };
  const { error } = await supabase.from('tenants').update(patch).eq('id', input.tenantId);
  if (error) {
    console.warn('[tenantSettings] champs extra non enregistrés (migration manquante ?):', error.message);
    return { ok: true };
  }
  return { ok: true };
}
